import { useCallback, useEffect, useRef } from 'react';

const DEFAULT_DEBOUNCE_MS = 300;

/**
 * react-select AsyncSelect loadOptions helper: debounce typing and abort
 * the previous in-flight directory search when the query changes.
 *
 * @param {(query: string, signal: AbortSignal) => Promise<array>} searchFn
 * @param {number} [debounceMs]
 */
export function useDebouncedAbortableLoader(searchFn, debounceMs = DEFAULT_DEBOUNCE_MS) {
  const abortRef = useRef(null);
  const timerRef = useRef(null);
  const seqRef = useRef(0);
  const searchFnRef = useRef(searchFn);
  searchFnRef.current = searchFn;

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      abortRef.current?.abort();
    },
    []
  );

  return useCallback(
    (inputVal = '') =>
      new Promise(resolve => {
        if (timerRef.current) clearTimeout(timerRef.current);
        abortRef.current?.abort();

        const q = String(inputVal || '').trim();
        if (!q) {
          resolve([]);
          return;
        }

        const seq = ++seqRef.current;
        timerRef.current = setTimeout(async () => {
          const controller = new AbortController();
          abortRef.current = controller;

          try {
            const options = await searchFnRef.current(q, controller.signal);
            if (seq !== seqRef.current) {
              resolve([]);
              return;
            }
            resolve(Array.isArray(options) ? options : []);
          } catch {
            if (seq === seqRef.current) resolve([]);
          }
        }, debounceMs);
      }),
    [debounceMs]
  );
}
